import React, { useContext, useEffect, useRef, useState } from "react";
import {
  useCreate,
  useDataProvider,
  useGetIdentity,
  useGetOne,
  useNotify,
  useRefresh,
  useStore,
} from "react-admin";
import Dropzone from "react-dropzone";
import { FaFileUpload } from "react-icons/fa";
import { useQueryClient } from "react-query";
import { useDispatch, useSelector } from "react-redux";
import {
  fileUploadingComplete,
  fileUploadingStart,
} from "../../../Store/Slice/fileUploadingSlice";
import { RootState } from "../../../Store/store";
import { useParams } from "react-router-dom";
import { setUploadPetImageId } from "../../../Store/Slice/applicantDetailsSlice";
import { JSONTree } from "react-json-tree";
import { useUserRole } from "../../../ContextApi/UserRoleContext";
import { markPendingDocumentUpload } from "../documentSubmitUtils";
import {
  BACK_IMAGE_MAX_BYTES,
  BackBarcodeWarningDialog,
  readBackBarcodeWarning,
} from "./backBarcodeWarning";

export interface uploadDocumentInterface {
  id: number;
  files: any;
  document_id: number;
  source_id: number;
  allowOfferLetter: boolean | null;
}

// Application statuses that mean manual review has already produced a determination
// (Verified / Further Documentation Required / Unverifiable / Completed-*). Uploading or
// replacing a document while in one of these statuses must force the application back to
// Review Pending so the new document is reassessed before a Subscriber sees it again
// (Story 4622 AC5) - regardless of application type or the uploading actor's role.
const REVIEWED_STATUSES = new Set([
  "13",
  "14",
  "15",
  "16",
  "17",
  "18",
]);


// Actor roles for which core's own reviewed-status reset (Story 4604,
// postUploadStatusPolicy.js REVIEWED_RESET_ACTOR_ROLES) already moves a reviewed Doc Upload
// (VR) application back to In Progress (status 2) as a side effect of the upload request
// itself. In Progress is already inside screeningScore.ts's preReviewStatuses, so dashboard
// hiding still works without us also forcing a change here - doing so would just overwrite
// core's write and skip the 4460 auto-submit cron's eligible-status window. Docuverus Super
// Admin/reviewer is intentionally NOT in this set: core's policy leaves status untouched for
// that actor, but AC5 still wants their own upload to re-hide, so the forced update below
// must still fire for them.
const DOC_UPLOAD_RESET_HANDLED_ACTOR_ROLES = new Set([
  "co-applicant",
  "co-signer",
  "subscriber user",
  "subscriber admin",
  "agent",
  "manager",
  "admin",
]);

async function refreshUploadQueries(queryClient: any, invalidateQueries: any) {
  if (!invalidateQueries) {
    return;
  }

  const queryKey = `${invalidateQueries}`;
  await Promise.all([
    queryClient.invalidateQueries?.([queryKey], undefined, {
      throwOnError: true,
    }),
    queryClient.invalidateQueries?.(queryKey, undefined, {
      throwOnError: true,
    }),
  ]);
}

export default function DragDropDocumentButton({
  trn_id,
  fetchRecord,
  documentId,
  SourceId,
  acceptedFiles = `image/*,application/pdf`,
  disabled = false,
  fileCount = 0,
  invalidateQueries,
  tag,
  label = "File Upload",
  helperText,
  fileError,
  concealed = false,
  allowOfferLetter = false,
  beforeUploadCallback = () => { },
  ...props
}: {
  [key: string]: any;
  helperText?: React.ReactNode;
}) {

  //Fix co-applicant issue
  const trnId = trn_id //useSelector((state: RootState) => state.users.userTrnId);
  // TODO : needed to replace
  const dispatch = useDispatch();

  const { identity, isLoading: identityLoading } = useGetIdentity();

  const notify = useNotify();
  const dataProvider = useDataProvider();
  const [barcodeWarning, setBarcodeWarning] = useState<ReturnType<typeof readBackBarcodeWarning>>(null);
  const pendingBarcodeWarning = useRef<ReturnType<typeof readBackBarcodeWarning>>(null);
  const inputRef = React.useRef<HTMLDivElement>(null);
  const [create, { data, isLoading, error }] = useCreate();
  const isOpeningForDashboard = useSelector(
    (state: RootState) => state.app.isOpeningForDashboard
  );
  const applicationStatus = useSelector(
    (state: any) => state?.users?.applicationStatus
  );
  const [disableFlag, setDisabledFlag] = React.useState(false);
  const { id, roletype, appType } = useParams();
  const { role: userRoleContext } = useUserRole();
  const userRoleRedux = useSelector((state: RootState) => state.users.role);
  const userRole = userRoleContext || userRoleRedux;
  const {
    data: applicantDatasub,
    isLoading: loading,
    refetch,
  } = useGetOne("getApplicantDetails", { id: trnId }, { enabled: !!trnId });
  const currentApplicationStatus =
    applicantDatasub?.Application_details?.application_status ??
    applicationStatus;

  const [updateApplicant, updateApplicantStatus] = useCreate(
    "updateApplicationStatus",
    {
      data: {
        trn_id: trnId,
        role: identity?.super_user_role,
        status: "5",
        office_property_name: applicantDatasub?.Application_details?.office_property_name,

      },
    },
    {
      onError: (error: any) => {
        notify(`${error.message}`, {
          type: "warning",
          anchorOrigin: { vertical: "top", horizontal: "right" },
        });
      },
      onSettled: () => {
        setTimeout(() => {
          dispatch(fileUploadingComplete());
        }, 3000);
      },
    }
  );

  const statusUpdate = () => {


    create(
      "updateApplicationStatus",
      {
        data: {
          trn_id: trnId,
          role: identity?.super_user_role,
          status: currentApplicationStatus,
          office_property_name: applicantDatasub?.Application_details?.office_property_name,

        },
      },

      {

        onError: (e: any) =>
          notify(`Operation fail:${e.message}`, {
            type: "error",
            anchorOrigin: { vertical: "top", horizontal: "right" },
          }),
        onSettled: () => {
          setTimeout(() => {
            dispatch(fileUploadingComplete());
          }, 3000);
        },
      }
    );
  };


  const [updateApplicantInProg, updateApplicantStatusInProg] = useCreate(
    "updateApplicationStatus",
    {
      data: {
        trn_id: trnId,
        role: identity?.super_user_role,
        status: "2",
        office_property_name: applicantDatasub?.Application_details?.office_property_name,


      },
    },
    {
      onError: (error: any) => {
        notify(`${error.message}`, {
          type: "warning",
          anchorOrigin: { vertical: "top", horizontal: "right" },
        });
      },
      onSettled: () => {
        setTimeout(() => {
          dispatch(fileUploadingComplete());
        }, 3000);
      },
    }
  );

  const queryClient = useQueryClient();
  const refresh = useRefresh();
  const isApplicationInviteApplication =
    appType === "1" ||
    applicantDatasub?.Application_details?.application_type === "Application Invite";
  const isDocUploadApplication =
    appType === "2" ||
    applicantDatasub?.Application_details?.application_type === "Doc Upload";
  const isAlreadyReviewed = REVIEWED_STATUSES.has(
    String(currentApplicationStatus)
  );
  const shouldPreserveReviewedAtsStatusOnUpload =
    isApplicationInviteApplication &&
    REVIEWED_STATUSES.has(String(currentApplicationStatus));
  const isDocUploadReviewedResetHandledByCore =
    isDocUploadApplication &&
    isAlreadyReviewed &&
    DOC_UPLOAD_RESET_HANDLED_ACTOR_ROLES.has(
      String(userRole || "").trim().toLowerCase()
    );
  const shouldSkipSuperAdminAutoReviewPending =
    userRole === "Docuverus Super Admin" &&
    String(currentApplicationStatus) === "5" &&
    (appType === "2" ||
      appType === "3" ||
      applicantDatasub?.Application_details?.application_type === "Doc Upload");
  const shouldAutoUpdateStatusOnUpload =
    !(isDocUploadApplication && userRole === "applicant");

  React.useEffect(() => {
    if (fileError) inputRef?.current?.scrollIntoView();
  }, [fileError]);

  const isParentFieldsetDisabled = (element) => {
    let parent = element.parentElement;
    while (parent) {
      if (parent.tagName === 'FIELDSET' && parent.disabled) {
        return true; // A parent fieldset is disabled
      }
      parent = parent.parentElement;
    }
    return false; // No parent fieldset is disabled
  };
  const [isParentFieldsetLocked, setParentFieldsetLocked] = useState(false);
  useEffect(() => {
    // Keep this in sync with parent fieldset state; super-admin add/remove source toggles it.
    const inputElement = inputRef.current;
    if (inputElement && isParentFieldsetDisabled(inputElement)) {
      setParentFieldsetLocked(true);
    } else {
      setParentFieldsetLocked(false);
    }
  });


  const beginUpload = (acceptedFiles) => {
            const totalSize = acceptedFiles.reduce(
              (acc, file) => acc + file.size,
              0
            );
            if (totalSize > 10 * 1024 * 1024) {
              notify(`File size exceeds the limit of 10MB`, {
                type: "error",
                anchorOrigin: { vertical: "top", horizontal: "right" },
              });
              return;
            }

            if (fileCount + acceptedFiles.length > 8) {
              notify("You've exceeded the upload limit.", {
                type: "warning",
                anchorOrigin: { vertical: "top", horizontal: "right" },
              });

              return;
            }

            notify("File Uploading...", {
              type: "info",
              autoHideDuration: 4000,
              anchorOrigin: { vertical: "top", horizontal: "right" },
            });
            dispatch(fileUploadingStart());
            if (
              identity?.super_user_role != "applicant" &&
              tag == "IncomeUpload"
            )
              beforeUploadCallback();
            setDisabledFlag(true);
            create(
              "fileUpload",
              {
                data: {
                  files: acceptedFiles,
                  trn_id: trnId,
                  document_id: documentId,
                  source_id: SourceId,
                  allowOfferLetter: allowOfferLetter,
                },
              },
              {
                onError: (error: any) => {
                  notify(`${error.message}`, {
                    type: "error",
                    anchorOrigin: { vertical: "top", horizontal: "right" },
                  });
                  setDisabledFlag(false);
                },
                onSuccess: (data) => {
                  markPendingDocumentUpload(trnId);
                  const warning = readBackBarcodeWarning(documentId, data?.records);
                  if (warning) {
                    setBarcodeWarning(warning);
                  }
                  refresh();
                  refreshUploadQueries(queryClient, invalidateQueries)
                    .then(() => {
                      if (!warning) {
                        notify("Document Uploaded", {
                          type: "success",
                          anchorOrigin: { vertical: "top", horizontal: "right" },
                        });
                      }
                    })
                    .catch(() => {
                      notify(
                        "Document uploaded, but the list refresh failed. Please refresh the page.",
                        {
                          type: "warning",
                          anchorOrigin: { vertical: "top", horizontal: "right" },
                        }
                      );
                    });
                  if (props.from == "petsImage") {
                    dispatch(
                      setUploadPetImageId({
                        index: props?.petIndex,
                        id: data?.records?.id,
                        file: data?.records?.blob_path,
                        type: "UPLOAD",
                      })
                    );
                  }
                  if (props?.handleOnChange) {
                    props?.handleOnChange();
                  }
                  if (
                    shouldAutoUpdateStatusOnUpload &&
                    !shouldSkipSuperAdminAutoReviewPending &&
                    currentApplicationStatus >= 3
                  ) {

                    if (
                      currentApplicationStatus !== 8 &&
                      currentApplicationStatus !== 7
                    ) {
                      if (userRole != "applicant") {
                        if (isApplicationInviteApplication) {
                          // ATS apps must never move to Review Pending on upload/replace.
                          // Keep Submitted / Screening / current reviewed outcome.
                          // Detect ATS from application_type as well as route appType —
                          // the DA review-queue URL (/admin/docupload_admin/...) has no appType.
                          if (!shouldPreserveReviewedAtsStatusOnUpload) {
                            statusUpdate();
                          }
                        } else if (isAlreadyReviewed) {
                          if (!isDocUploadReviewedResetHandledByCore) {
                            updateApplicant();
                          }
                          // else: core's own reset (Story 4604) already moved this back to
                          // In Progress synchronously during the upload request itself -
                          // nothing left to do here.
                        } else {
                          updateApplicant();
                        }
                      }
                    }
                  }

                  if (shouldAutoUpdateStatusOnUpload && currentApplicationStatus == 1) {
                    updateApplicantInProg();
                  }
                  // if (identity?.super_user_role == "Subscriber Admin" && identity?.super_user_role != "applicant")
                  //   handleApplicantSubmit();
                  setTimeout(() => setDisabledFlag(false), 5000);
                },
              }
            );
  };

  const retakeBarcodePhoto = () => {
    const documentRowId = barcodeWarning?.documentId;
    if (!documentRowId) {
      notify("Please delete the current image and try again.", {
        type: "warning",
        anchorOrigin: { vertical: "top", horizontal: "right" },
      });
      return;
    }
    pendingBarcodeWarning.current = barcodeWarning;
    setBarcodeWarning(null);
  };

  const uploadReplacement = (file: File) => {
    const documentRowId = pendingBarcodeWarning.current?.documentId;
    if (!documentRowId) {
      return;
    }
    if (file.size > BACK_IMAGE_MAX_BYTES) {
      notify("File is larger than 10MB", {
        type: "warning",
        anchorOrigin: { vertical: "top", horizontal: "right" },
      });
      setBarcodeWarning(pendingBarcodeWarning.current);
      return;
    }
    dataProvider
      .deleteDocument(documentRowId)
      .then(() => {
        pendingBarcodeWarning.current = null;
        props.onBackImageDeleted?.();
        refresh();
        return refreshUploadQueries(queryClient, invalidateQueries)
          .catch(() => undefined)
          .then(() => {
            beginUpload([file]);
          });
      })
      .catch(() => {
        notify("We couldn't remove the current image. Please re-upload again.", {
          type: "error",
          anchorOrigin: { vertical: "top", horizontal: "right" },
        });
        setBarcodeWarning(pendingBarcodeWarning.current);
      });
  };

  return (
    <>
      <div style={concealed ? { display: "none" } : undefined}>
      <small
        className="mute print-hide"
        style={{
          display: "block",
          width: "100px",
          margin: "0 auto 6px",
          fontSize: "11px",
          fontWeight: 600,
          textAlign: "center",
          color: "#4b5563",
          letterSpacing: "0.01em",
        }}
      >
        {label}
      </small>
      <div className="pb-1 print-hide" ref={inputRef}>
        <Dropzone
          // maxFiles={8}
          maxSize={10485760} //10MB in bytes
          multiple={props?.multiple ? false : true}
          disabled={Boolean(disabled || disableFlag || isParentFieldsetLocked)}
          accept={acceptedFiles}
          onDropRejected={(file: any[]) => {
            for (let f of file) {
              for (let err of f.errors) {
                if (err.code == "file-too-large") {
                  notify('File is larger than 10MB', {
                    type: "warning",
                    anchorOrigin: { vertical: "top", horizontal: "right" },
                  });
                  return;
                }
              }
            }

            const errorMessage = file[0]?.errors
              .map((item) => item?.message)
              .join(", ");
            notify(errorMessage, {
              type: "warning",
              anchorOrigin: { vertical: "top", horizontal: "right" },
            });
          }}
          onDropAccepted={beginUpload}
        >
          {({ getRootProps, getInputProps }) => {
            const helperStyle: React.CSSProperties = {
              marginTop: "8px",
              width: "100%",
              maxWidth: "170px",
              padding: "8px 10px",
              borderRadius: 6,
              backgroundColor: "#fff3cd",
              color: "#664d03",
              fontSize: "13px",
              fontWeight: 400,
              lineHeight: "1.35",
              textAlign: "left",
              whiteSpace: "normal",
              overflowWrap: "anywhere",
            };
            return (
              <div
                style={{ display: "flex", flexDirection: "column", alignItems: "center" }}
              >
                <div
                  className="docs-upload"
                  style={{
                    width: "112px",
                    minWidth: "112px",
                    maxWidth: "112px",
                    background: "#fff",
                  }}
                >
                  <div className="upload-subwrap" {...getRootProps()}>
                    <span className="upload-file-icon">
                      <FaFileUpload color="grey" size={36} />
                    </span>
                    {isLoading ? (
                      <div className="mt-1 d-flex align-items-center justify-content-center">
                        &nbsp;
                        <div
                          className="spinner-grow spinner-grow-sm"
                          role="status"
                        ></div>
                        <p style={{ fontSize: "10px" }}>&nbsp;Uploading...</p>
                      </div>
                    ) : (
                      <>
                        <p className="upload-primary-action" style={{ background: "#fff" }}>
                          Tap or Click to Upload
                        </p>
                        <p className="upload-secondary-action" style={{ background: "#fff" }}>
                          Drop files here
                        </p>
                      </>
                    )}
                    <input {...getInputProps()} type="file" name="" id="" />
                  </div>
                </div>
                {helperText ? <div style={helperStyle}>{helperText}</div> : null}
              </div>
            );
          }}
        </Dropzone>

        {fileError && (
          <div
            className="invalid-feedback bg-danger text-white mb-2 d-inline"
            style={{
              display: "block",
              fontSize: "80%",
              marginTop: 14,
              fontWeight: 450,
            }}
          >
            {"File required"}
          </div>
        )}
      </div>
      </div>
      <BackBarcodeWarningDialog
        warning={barcodeWarning}
        onDismiss={() => setBarcodeWarning(null)}
        onRetake={retakeBarcodePhoto}
        onReplacementSelected={uploadReplacement}
      />
    </>
  );
}
